import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import FormError from '@/components/shared/FormError.tsx';

interface ConfirmDestructiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string | ReactNode;
  confirmLabel: string;
  confirmingLabel?: string;
  cancelLabel?: string;
  errorFallback?: string;
  size?: 'sm' | 'md';
}

export default function ConfirmDestructiveModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmingLabel,
  cancelLabel,
  errorFallback,
  size = 'sm',
}: ConfirmDestructiveModalProps) {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) setSubmitError(null);
  }, [isOpen]);

  const handleConfirm = async () => {
    setSubmitError(null);
    setIsConfirming(true);
    try {
      await onConfirm();
    } catch (err) {
      setSubmitError(
        err instanceof Error && err.message
          ? err.message
          : errorFallback ?? t('common.unexpectedError', 'Ha ocurrido un error inesperado'),
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isConfirming) onClose();
  };

  const sizeClass = size === 'md' ? 'sm:max-w-md' : 'sm:max-w-sm';
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const resolvedConfirmingLabel = confirmingLabel ?? confirmLabel;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className={sizeClass} aria-describedby={descriptionId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" aria-hidden="true" />
            {title}
          </DialogTitle>
          <DialogDescription id={descriptionId}>{description}</DialogDescription>
        </DialogHeader>

        <FormError
          message={submitError}
          onDismiss={() => setSubmitError(null)}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isConfirming}
          >
            {resolvedCancelLabel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isConfirming}
            aria-busy={isConfirming}
          >
            {isConfirming ? resolvedConfirmingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
